import { Injectable, inject } from '@angular/core';
import { MultiselectItem } from '@ui/aos-lib';
import { combineLatest, catchError, of, switchMap, Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { isSingleCommodityClient } from 'src/app/pages/help-and-settings/user-settings/store/user-settings.reducer';
import { Commodity } from '@portal/shared/types/commodity.types';

import { ReportGroupFilterListingService } from 'src/app/services/api/report-group-filter/report-group-filter-listing.service';
import { translationKeysMap } from 'src/app/services/translate/translate.key-mapper';
import {
  FilterModel,
  FilterModels,
  ListingFiltersSource
} from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { CustomersService } from './services/customers.service';
import {
  commoditiesFilterModel,
  customerStatusFilterModel,
  customerTypeFilterModel,
  onDemandReadsStatusFilterModel
} from './customers.constants';

@Injectable()
export class CustomersListingFiltersSourceService implements ListingFiltersSource {
  private readonly customersService = inject(CustomersService);
  readonly reportGroupFilterService = inject(ReportGroupFilterListingService);
  readonly #store = inject(Store);
  readonly #isSingleCommodityClient = this.#store.selectSignal(isSingleCommodityClient);

  readonly #applicable = 'AMI Health Dashboard';

  getAll(): Observable<FilterModels> {
    return combineLatest([this.customersService.getCustomerFiltersItems(), this.getReportGroup()]).pipe(
      switchMap(([{ customerTypes, commodities }, reportGroupFilter]) => {
        const mapToMultiselectItems = (items: string[]): MultiselectItem[] =>
          items.map(item => ({ text: translationKeysMap.get(item) || item, value: item }));
        const customerTypeItems: MultiselectItem[] = mapToMultiselectItems(customerTypes);
        const commodityItems: MultiselectItem[] = mapToMultiselectItems(
          commodities.filter(c => c !== Commodity.Communication)
        );
        const customerTypeFilter: FilterModel = {
          ...customerTypeFilterModel,
          items: customerTypeItems,
          selected: customerTypeItems
        };
        const commoditiesFilter: FilterModel = {
          ...commoditiesFilterModel,
          items: commodityItems,
          selected: commodityItems
        };
        const filters: FilterModels = [
          reportGroupFilter,
          customerStatusFilterModel,
          customerTypeFilter,
          ...(this.#isSingleCommodityClient() ? [] : [commoditiesFilter]),
          onDemandReadsStatusFilterModel
        ];
        return of(filters);
      }),
      catchError(() => {
        return of([]);
      })
    );
  }

  getReportGroup(): Observable<FilterModel> {
    return this.reportGroupFilterService.getFilter(this.#applicable);
  }
}
